import React, { useState, useMemo } from 'react';
import styled from 'styled-components';

const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const SearchFilters = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
`;

const FilterTag = styled.span`
  background: #007bff;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 12px;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }
`;

const ResultsCount = styled.div`
  color: #666;
  font-size: 14px;
  margin-top: 10px;
`;

const PropertySearch = ({ properties, onFilteredProperties, isAdmin = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [occupancyFilter, setOccupancyFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');

  // Get unique agents if admin
  const uniqueAgents = useMemo(() => {
    if (!isAdmin) return [];
    const agents = new Set();
    properties.forEach(property => {
      if (property.agent_email) {
        agents.add(`${property.agent_email}|${property.agent_first_name} ${property.agent_last_name}`);
      }
    });
    return Array.from(agents).map(agent => {
      const [email, name] = agent.split('|');
      return { email, name };
    });
  }, [properties, isAdmin]);

  // Filter properties based on search criteria
  const filteredProperties = useMemo(() => {
    let filtered = properties;

    // Text search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(property => {
        return (
          property.address_line1?.toLowerCase().includes(searchLower) ||
          property.unit_number?.toLowerCase().includes(searchLower) ||
          property.city?.toLowerCase().includes(searchLower) ||
          property.state?.toLowerCase().includes(searchLower) ||
          property.zip_code?.includes(searchTerm) ||
          property.tenant_name?.toLowerCase().includes(searchLower) ||
          property.tenant_email?.toLowerCase().includes(searchLower) ||
          property.agent_email?.toLowerCase().includes(searchLower) ||
          property.agent_first_name?.toLowerCase().includes(searchLower) ||
          property.agent_last_name?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Status filter - disabled as it conflicts with the main dashboard filter
    // if (statusFilter !== 'all') {
    //   filtered = filtered.filter(property => property.status === statusFilter);
    // }

    // Occupancy filter
    if (occupancyFilter !== 'all') {
      const isOccupied = occupancyFilter === 'occupied';
      filtered = filtered.filter(property => property.is_occupied === isOccupied);
    }

    // Agent filter (admin only)
    if (agentFilter !== 'all' && isAdmin) {
      filtered = filtered.filter(property => property.agent_email === agentFilter);
    }

    return filtered;
  }, [properties, searchTerm, occupancyFilter, agentFilter, isAdmin]);

  // Update parent component when filtered properties change
  React.useEffect(() => {
    onFilteredProperties(filteredProperties);
  }, [filteredProperties, onFilteredProperties]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setOccupancyFilter('all');
    setAgentFilter('all');
  };

  const activeFiltersCount = [
    searchTerm.trim() ? 1 : 0,
    occupancyFilter !== 'all' ? 1 : 0,
    agentFilter !== 'all' ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  return (
    <SearchContainer>
      {properties.length > 0 && (
        <SearchInput
          type="text"
          placeholder="🔍 Search properties by address, city, or agent..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      )}
      
      {properties.length > 0 && (
        <>
          <SearchFilters>
        {/* Status filter removed - using main dashboard filter buttons instead */}

        <FilterSelect
          value={occupancyFilter}
          onChange={(e) => setOccupancyFilter(e.target.value)}
        >
          <option value="all">All Properties</option>
          <option value="occupied">Occupied</option>
          <option value="vacant">Vacant</option>
        </FilterSelect>

        {isAdmin && uniqueAgents.length > 0 && (
          <FilterSelect
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
          >
            <option value="all">All Agents</option>
            {uniqueAgents.map(agent => (
              <option key={agent.email} value={agent.email}>
                {agent.name} ({agent.email})
              </option>
            ))}
          </FilterSelect>
        )}

        {activeFiltersCount > 0 && (
          <FilterTag>
            {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active
            <ClearButton onClick={clearAllFilters}>✕</ClearButton>
          </FilterTag>
        )}
      </SearchFilters>

      <ResultsCount>
        Showing {filteredProperties.length} of {properties.length} properties
        {isAdmin && (
          <span style={{ marginLeft: '10px', color: '#007bff' }}>
            👑 Admin View - All Properties
          </span>
        )}
          </ResultsCount>
        </>
      )}
    </SearchContainer>
  );
};

export default PropertySearch;